import React from "react";
import { createPortal } from "react-dom";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { calculateTimesheetApprovalProgress } from "../utils/timesheetProgress";

const formatHours = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) {
    return "0";
  }
  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2).replace(/\.?0+$/, "");
};

const TimesheetApprovalProgress = ({
  holidays = [],
  monthTimesheet = [],
  selectedTimesheetMonth,
  selectedTimesheetMonthId,
}) => {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const dialogRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const progress = React.useMemo(
    () =>
      calculateTimesheetApprovalProgress({
        holidays,
        monthId: selectedTimesheetMonthId || selectedTimesheetMonth?.id,
        monthTimesheet,
      }),
    [holidays, monthTimesheet, selectedTimesheetMonth, selectedTimesheetMonthId]
  );

  React.useEffect(() => {
    if (!detailsOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const triggerElement = triggerRef.current;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setDetailsOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    window.requestAnimationFrame(() => dialogRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      triggerElement?.focus();
    };
  }, [detailsOpen]);

  if (!progress.targetHours) {
    return null;
  }

  const submittedRatio = `${formatHours(progress.submittedHours)}/${formatHours(progress.targetHours)}`;
  const riskLabel = `${formatHours(progress.lopDays)} LOP ${Number(progress.lopDays) === 1 ? "day" : "days"}`;
  const severeLopRisk = progress.hasLopRisk && Number(progress.lopDays) > 2;

  return (
    <>
      <button
        ref={triggerRef}
        className={[
          "new-hrm-approval-progress",
          progress.hasLopRisk ? "new-hrm-approval-progress--risk" : "",
          severeLopRisk ? "new-hrm-approval-progress--severe" : "",
        ].filter(Boolean).join(" ")}
        type="button"
        onClick={() => setDetailsOpen(true)}
        aria-label={`Timesheet submission progress, ${submittedRatio} submitted hours over targeted hours${progress.hasLopRisk ? `, ${severeLopRisk ? "critical payroll risk, " : ""}${riskLabel}` : ""}`}
        title="Open time approval risk details"
      >
        {severeLopRisk && (
          <span className="new-hrm-approval-progress__threat">Critical payroll risk</span>
        )}
        <span className="new-hrm-approval-progress__label">
          <strong>{submittedRatio}</strong>
          {progress.hasLopRisk && (
            <em>
              <FiAlertTriangle aria-hidden="true" />
              {riskLabel}
            </em>
          )}
        </span>
        <span className="new-hrm-approval-progress__bar" aria-hidden="true">
          <span style={{ width: `${progress.progressPercent}%` }} />
        </span>
      </button>

      {detailsOpen && typeof document !== "undefined" && createPortal(
        <div className="new-hrm-modal-portal new-hrm-app--premium">
          <div
            className="new-hrm-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setDetailsOpen(false);
              }
            }}
          >
            <section
              ref={dialogRef}
              className="new-hrm-modal new-hrm-lop-risk-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="lop-risk-title"
              aria-describedby="lop-risk-description"
              tabIndex="-1"
            >
              <header className="new-hrm-modal__header">
                <div>
                  <p className="new-hrm-modal-note">Timesheet approval target</p>
                  <h2 id="lop-risk-title">Monthly submitted hours</h2>
                  <p id="lop-risk-description">{progress.targetWorkingDays} workdays through today. Target is {formatHours(progress.targetHours)} submitted hours.</p>
                </div>
                <button className="new-hrm-icon-button" type="button" onClick={() => setDetailsOpen(false)} aria-label="Close approval details">
                  <FiX aria-hidden="true" />
                </button>
              </header>

            <div className="new-hrm-lop-risk-modal__summary">
              <span>
                <small>Submitted</small>
                <strong>{formatHours(progress.submittedHours)}h</strong>
              </span>
              <span>
                <small>Target through today</small>
                <strong>{formatHours(progress.targetHours)}h</strong>
              </span>
              <span className={progress.hasLopRisk ? "new-hrm-lop-risk-modal__risk" : ""}>
                <small>Missing</small>
                <strong>{formatHours(progress.remainingHours)}h</strong>
              </span>
              <span className={progress.hasLopRisk ? "new-hrm-lop-risk-modal__risk" : ""}>
                <small>LOP days</small>
                <strong>{formatHours(progress.lopDays)}</strong>
              </span>
            </div>

            {progress.hasLopRisk ? (
              <>
                <p className={`new-hrm-lop-risk-modal__warning ${severeLopRisk ? "new-hrm-lop-risk-modal__warning--severe" : ""}`}>
                  <FiAlertTriangle aria-hidden="true" />
                  Critical payroll risk: HRM can create approved LOP leaves for {formatHours(progress.lopDays)} day{Number(progress.lopDays) === 1 ? "" : "s"} if these TSE hours stay missing. This can directly reduce salary. Submit the missing TSEs now.
                </p>
                {progress.submissionDays.length > 0 ? (
                  <div className="new-hrm-lop-risk-table" role="table" aria-label="Submitted timesheet hours">
                    <div role="row" className="new-hrm-lop-risk-table__head">
                      <span role="columnheader">Date</span>
                      <span role="columnheader">Target</span>
                      <span role="columnheader">Submitted</span>
                      <span role="columnheader">Missing</span>
                    </div>
                    {progress.submissionDays.map((day) => (
                      <div role="row" key={day.dateKey}>
                        <span role="cell" data-label="Date">{day.label}</span>
                        <span role="cell" data-label="Target">{formatHours(day.expectedHours)}h</span>
                        <span role="cell" data-label="Submitted">{formatHours(day.submittedHours)}h</span>
                        <span role="cell" data-label="Missing">{formatHours(day.missingHours)}h</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="new-hrm-lop-risk-modal__clear new-hrm-lop-risk-modal__clear--warning">
                    Submit today's missing hours to clear the LOP risk.
                  </p>
                )}
              </>
            ) : (
              <p className="new-hrm-lop-risk-modal__clear">
                No LOP risk for elapsed workdays. Keep submitting entries until the monthly target is complete.
              </p>
            )}
            </section>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TimesheetApprovalProgress;
